import { type RefObject, useEffect, useRef } from 'react';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * What a drawer marked `aria-modal` promises and none of ours did: focus moves
 * INTO it when it opens, Tab stays inside it while it is open, Escape closes
 * it, and focus goes BACK to whatever opened it when it closes.
 *
 * Without the first, a keyboard or screen-reader user opened a drawer and was
 * still on the button behind it; without the second, Tab walked out of the
 * dialog into a page they had been told was inert; without the last, closing it
 * dropped them at the top of the document, a long way from the row they were
 * working through.
 *
 * `onClose` is read through a ref, so a parent that passes a fresh arrow every
 * render does not re-run the effect -- which would steal focus back to the
 * dialog's first control on every poll.
 */
export function useDialog(ref: RefObject<HTMLElement | null>, onClose: () => void, active = true) {
  const close = useRef(onClose);
  close.current = onClose;

  // Who opened it, read while rendering the open -- before a field inside
  // with autoFocus has taken focus, which would make the field the "opener".
  const opener = useRef<HTMLElement | null>(null);
  const wasActive = useRef(false);
  if (active && !wasActive.current && typeof document !== 'undefined') {
    opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  }
  wasActive.current = active;

  useEffect(() => {
    if (!active) return;
    const returnTo = opener.current;
    const dialog = ref.current;
    if (dialog) {
      // The dialog itself rather than its first control: landing on "Close"
      // or "Resolve" by default is one stray Enter from doing it. Unless focus
      // is inside it already -- a field it opened with autoFocus keeps it.
      if (!dialog.hasAttribute('tabindex')) dialog.setAttribute('tabindex', '-1');
      if (!dialog.contains(document.activeElement)) dialog.focus({ preventScroll: true });
    }

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        close.current();
        return;
      }
      if (event.key !== 'Tab' || !ref.current) return;
      const items = [...ref.current.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
        (item) => item.offsetParent !== null || item === document.activeElement,
      );
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      const at = document.activeElement;
      if (event.shiftKey && (at === first || at === ref.current)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && at === last) {
        event.preventDefault();
        first.focus();
      } else if (!ref.current.contains(at)) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      // Only if it is still in the page: the row that opened it may have gone
      // with the poll that closed it.
      if (returnTo?.isConnected) returnTo.focus({ preventScroll: true });
    };
  }, [ref, active]);
}
